import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  SavedSearch,
  SearchCriteria,
  SearchEntityType
} from '../../models/file-vault.models';

export interface SavedSearchAction {
  action: 'load' | 'save' | 'delete' | 'share' | 'unshare';
  savedSearch?: SavedSearch;
  criteria?: SearchCriteria;
}

@Component({
  selector: 'fv-saved-searches',
  standalone: true,
  template: `
    <div class="saved-searches-container">
      <!-- Header -->
      <div class="header">
        <span class="header-title">Saved Searches</span>
        <button kendoButton
                [look]="'flat'"
                (click)="openSaveDialog()"
                *ngIf="currentCriteria && showSaveButton"
                title="Save current search">
          + Save
        </button>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">Loading...</div>

      <!-- Saved Searches List -->
      <div class="saved-list" *ngIf="!isLoading">
        <div class="empty-state" *ngIf="savedSearches.length === 0">
          <p>No saved searches yet</p>
          <p class="hint" *ngIf="currentCriteria">Click "Save" to save your current search</p>
        </div>

        <div class="saved-item"
             *ngFor="let search of savedSearches"
             [class.selected]="selectedSearch?.id === search.id"
             (click)="selectSearch(search)">
          <div class="item-info">
            <div class="item-header">
              <span class="item-name">{{ search.name }}</span>
              <span class="shared-badge" *ngIf="search.isShared" title="Shared">
                Shared
              </span>
            </div>
            <div class="item-query">"{{ search.query }}"</div>
            <div class="item-meta">
              <span class="meta-entity" *ngIf="search.criteria.entityType">
                {{ getEntityTypeLabel(search.criteria.entityType) }}
              </span>
              <span class="meta-date">{{ search.createdAt | date:'shortDate' }}</span>
            </div>
          </div>
          <div class="item-actions">
            <button kendoButton
                    [look]="'flat'"
                    (click)="loadSearch(search, $event)"
                    title="Load search">
              Load
            </button>
            <button kendoButton
                    [look]="'flat'"
                    (click)="toggleShare(search, $event)"
                    [title]="search.isShared ? 'Unshare' : 'Share'">
              {{ search.isShared ? 'Unshare' : 'Share' }}
            </button>
            <button kendoButton
                    [look]="'flat'"
                    (click)="confirmDelete(search, $event)"
                    title="Delete">
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Save Dialog -->
      <kendo-dialog *ngIf="showSaveDialog"
                    title="Save Search"
                    (close)="closeSaveDialog()"
                    [width]="400">
        <div class="save-dialog-content">
          <div class="form-field">
            <label>Search Name</label>
            <input kendoTextBox
                   [(ngModel)]="newSearchName"
                   placeholder="Enter a name for this search..."
                   class="name-input" />
          </div>

          <div class="form-field">
            <label class="checkbox-label">
              <input type="checkbox" [(ngModel)]="newSearchShared" />
              Share with others
            </label>
            <p class="hint">Shared searches are visible to all users</p>
          </div>

          <div class="search-preview">
            <div class="preview-label">Search Preview:</div>
            <div class="preview-content">
              <div class="preview-query">"{{ currentCriteria?.query }}"</div>
              <div class="preview-filters" *ngIf="hasFilters()">
                <span *ngIf="currentCriteria?.entityType">
                  Type: {{ getEntityTypeLabel(currentCriteria!.entityType!) }}
                </span>
                <span *ngIf="currentCriteria?.lifecycleState">
                  State: {{ currentCriteria!.lifecycleState }}
                </span>
                <span *ngIf="currentCriteria?.createdAfter">
                  After: {{ currentCriteria!.createdAfter | date:'shortDate' }}
                </span>
                <span *ngIf="currentCriteria?.createdBefore">
                  Before: {{ currentCriteria!.createdBefore | date:'shortDate' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeSaveDialog()">Cancel</button>
          <button kendoButton
                  [themeColor]="'primary'"
                  (click)="saveCurrentSearch()"
                  [disabled]="!newSearchName.trim()">
            Save Search
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Delete Confirmation Dialog -->
      <kendo-dialog *ngIf="showDeleteDialog"
                    title="Delete Saved Search"
                    (close)="closeDeleteDialog()"
                    [width]="350">
        <p>Are you sure you want to delete "{{ searchToDelete?.name }}"?</p>
        <p class="warning" *ngIf="searchToDelete?.isShared">
          This search is shared and will be removed for all users.
        </p>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeDeleteDialog()">Cancel</button>
          <button kendoButton
                  [themeColor]="'error'"
                  (click)="deleteSearch()">
            Delete
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>
  `,
  styles: [`
    .saved-searches-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
    }

    .header-title {
      font-weight: 600;
      color: var(--fv-text);
    }

    .loading {
      padding: 24px;
      text-align: center;
      color: var(--fv-text-secondary);
    }

    .saved-list {
      flex: 1;
      overflow-y: auto;
    }

    .empty-state {
      padding: 24px;
      text-align: center;
      color: var(--fv-text-secondary);
    }

    .empty-state p {
      margin: 4px 0;
    }

    .hint {
      font-size: 12px;
      color: var(--fv-text-muted);
    }

    .saved-item {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 12px;
      border-bottom: 1px solid var(--fv-border-subtle);
      cursor: pointer;
      transition: background 0.15s;
    }

    .saved-item:hover {
      background: var(--fv-surface-hover);
    }

    .saved-item.selected {
      background: var(--fv-status-info-bg);
    }

    .item-info {
      flex: 1;
      min-width: 0;
    }

    .item-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
    }

    .item-name {
      font-weight: 500;
      color: var(--fv-text);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .shared-badge {
      font-size: 10px;
      padding: 2px 6px;
      background: var(--fv-status-success-bg);
      color: var(--fv-status-success-fg);
      border-radius: 10px;
    }

    .item-query {
      font-size: 12px;
      color: var(--fv-text-secondary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-bottom: 4px;
    }

    .item-meta {
      display: flex;
      gap: 8px;
      font-size: 11px;
      color: var(--fv-text-muted);
    }

    .meta-entity {
      padding: 1px 6px;
      background: var(--fv-status-info-bg);
      color: var(--fv-status-info-fg);
      border-radius: 4px;
    }

    .item-actions {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
    }

    .item-actions button {
      font-size: 11px;
      padding: 4px 8px;
    }

    .save-dialog-content {
      padding: 8px 0;
    }

    .form-field {
      margin-bottom: 16px;
    }

    .form-field label {
      display: block;
      margin-bottom: 4px;
      font-weight: 500;
      color: var(--fv-text);
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
    }

    .checkbox-label input {
      margin: 0;
    }

    .name-input {
      width: 100%;
    }

    .search-preview {
      background: var(--fv-surface-hover);
      padding: 12px;
      border-radius: 4px;
    }

    .preview-label {
      font-size: 11px;
      color: var(--fv-text-secondary);
      margin-bottom: 4px;
      text-transform: uppercase;
    }

    .preview-query {
      font-weight: 500;
      color: var(--fv-text);
      margin-bottom: 4px;
    }

    .preview-filters {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .preview-filters span {
      padding: 2px 6px;
      background: var(--fv-surface-hover);
      border-radius: 4px;
    }

    .warning {
      color: var(--fv-status-warning-fg);
      font-size: 13px;
      background: var(--fv-status-warning-bg);
      padding: 8px;
      border-radius: 4px;
      margin-top: 8px;
    }
  `],
  imports: [CommonModule, FormsModule, InputsModule, ButtonsModule, DialogModule]
})
export class SavedSearchesComponent implements OnInit {
  private api = inject(FileVaultApiService);

  /** Current search criteria to save */
  @Input() currentCriteria?: SearchCriteria;

  /** Show save button in header */
  @Input() showSaveButton = true;

  /** Emitted when a saved search action occurs */
  @Output() searchAction = new EventEmitter<SavedSearchAction>();

  savedSearches: SavedSearch[] = [];
  isLoading = false;
  selectedSearch: SavedSearch | null = null;

  // Save dialog
  showSaveDialog = false;
  newSearchName = '';
  newSearchShared = false;

  // Delete dialog
  showDeleteDialog = false;
  searchToDelete: SavedSearch | null = null;

  ngOnInit(): void {
    this.loadSavedSearches();
  }

  async loadSavedSearches(): Promise<void> {
    this.isLoading = true;
    try {
      const searches = await this.api.getSavedSearches().toPromise();
      this.savedSearches = searches ?? [];
    } catch (error) {
      console.error('Failed to load saved searches', error);
      this.savedSearches = [];
    } finally {
      this.isLoading = false;
    }
  }

  selectSearch(search: SavedSearch): void {
    this.selectedSearch = this.selectedSearch?.id === search.id ? null : search;
  }

  loadSearch(search: SavedSearch, event: MouseEvent): void {
    event.stopPropagation();
    this.searchAction.emit({
      action: 'load',
      savedSearch: search,
      criteria: search.criteria
    });
  }

  async toggleShare(search: SavedSearch, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    try {
      const updated = await this.api.toggleSavedSearchSharing(search.id, !search.isShared).toPromise();
      if (updated) {
        const index = this.savedSearches.findIndex(s => s.id === search.id);
        if (index >= 0) {
          this.savedSearches[index] = updated;
        }
        this.searchAction.emit({
          action: search.isShared ? 'unshare' : 'share',
          savedSearch: updated
        });
      }
    } catch (error) {
      console.error('Failed to toggle sharing', error);
    }
  }

  confirmDelete(search: SavedSearch, event: MouseEvent): void {
    event.stopPropagation();
    this.searchToDelete = search;
    this.showDeleteDialog = true;
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog = false;
    this.searchToDelete = null;
  }

  async deleteSearch(): Promise<void> {
    if (!this.searchToDelete) return;

    try {
      await this.api.deleteSavedSearch(this.searchToDelete.id).toPromise();
      this.savedSearches = this.savedSearches.filter(s => s.id !== this.searchToDelete!.id);
      this.searchAction.emit({
        action: 'delete',
        savedSearch: this.searchToDelete
      });
      this.closeDeleteDialog();
    } catch (error) {
      console.error('Failed to delete saved search', error);
    }
  }

  openSaveDialog(): void {
    if (!this.currentCriteria) return;
    this.newSearchName = '';
    this.newSearchShared = false;
    this.showSaveDialog = true;
  }

  closeSaveDialog(): void {
    this.showSaveDialog = false;
    this.newSearchName = '';
    this.newSearchShared = false;
  }

  async saveCurrentSearch(): Promise<void> {
    if (!this.currentCriteria || !this.newSearchName.trim()) return;

    try {
      const saved = await this.api.saveSearch(
        this.newSearchName.trim(),
        this.currentCriteria,
        this.newSearchShared
      ).toPromise();

      if (saved) {
        this.savedSearches.unshift(saved);
        this.searchAction.emit({
          action: 'save',
          savedSearch: saved,
          criteria: this.currentCriteria
        });
      }
      this.closeSaveDialog();
    } catch (error) {
      console.error('Failed to save search', error);
    }
  }

  hasFilters(): boolean {
    if (!this.currentCriteria) return false;
    return !!(
      this.currentCriteria.entityType ||
      this.currentCriteria.lifecycleState ||
      this.currentCriteria.createdAfter ||
      this.currentCriteria.createdBefore ||
      this.currentCriteria.createdBy
    );
  }

  getEntityTypeLabel(entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.Part: return 'Parts';
      case SearchEntityType.Document: return 'Documents';
      case SearchEntityType.File: return 'Files';
      case SearchEntityType.Change: return 'Changes';
      default: return 'All';
    }
  }

  /** Public method to refresh the saved searches list */
  refresh(): void {
    this.loadSavedSearches();
  }

  /** Public method to set criteria for saving */
  setCriteria(criteria: SearchCriteria): void {
    this.currentCriteria = criteria;
  }
}
